import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { updateApiEndpoint, resetApiEndpoint, restoreApiEndpoint } from '../services/api';
import queryClient from '../queryClient';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      company: null,
      selectedCompany: null, // For Super/Managing Admins to work within a company context
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      _hasHydrated: false, // Track if persist has rehydrated
      
      // Set loading state
      setLoading: (loading) => set({ isLoading: loading }),
      
      // Check if user is a super user (Super Admin or Managing Admin)
      isSuperUser: () => {
        const { user } = get();
        return user && ['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role);
      },
      
      // Get the current working company (selected or user's own)
      getCurrentCompany: () => {
        const { user, company, selectedCompany } = get();
        if (user && ['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role)) {
          return selectedCompany || company;
        }
        return company;
      },
      
      // Get the current company ID for API calls
      getCurrentCompanyId: () => {
        const company = get().getCurrentCompany();
        return company?._id || company;
      },
      
      // Select a company to work within (for Super/Managing Admins)
      // Also updates the API endpoint to route to the company's regional Cloud Run
      selectCompany: async (company) => {
        set({ selectedCompany: company });
        
        // Update API endpoint to match the selected company's region
        // This ensures file uploads go to the correct regional Redis
        if (company) {
          console.log(`[AuthStore] Switching to company: ${company.name || company._id}`);
          // Clear stale session storage before fetching new endpoint
          sessionStorage.removeItem('neoshift-regional-endpoint');
          await updateApiEndpoint();
        }
      },
      
      // Clear selected company and reset to user's default region
      clearSelectedCompany: async () => {
        set({ selectedCompany: null });
        // Clear session storage and re-fetch endpoint for user's own company
        sessionStorage.removeItem('neoshift-regional-endpoint');
        await updateApiEndpoint();
      },
      
      // Login
      login: async (email, password) => {
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, company, accessToken, refreshToken } = response.data;
          
          // Clear any cached data from previous session
          queryClient.clear();
          
          set({
            user,
            company,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          // Update API endpoint for regional routing (optimizes BigQuery costs)
          // This runs async - doesn't block login
          updateApiEndpoint().catch(err => {
            console.warn('[Auth] Regional routing update failed:', err);
          });
          
          return { success: true };
        } catch (error) {
          return { 
            success: false, 
            error: error.response?.data?.error || 'Login failed' 
          };
        }
      },
      
      // Logout
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear all cached React Query data
          queryClient.clear();
          
          // Reset API endpoint to default (clears regional routing)
          resetApiEndpoint();
          
          set({
            user: null,
            company: null,
            selectedCompany: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false
          });
        }
      },
      
      // Refresh token
      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          set({ isAuthenticated: false, isLoading: false });
          return false;
        }
        
        try {
          const response = await api.post('/auth/refresh-token', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          
          set({
            accessToken,
            refreshToken: newRefreshToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          return true;
        } catch (error) {
          set({
            user: null,
            company: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false
          });
          return false;
        }
      },
      
      // Fetch current user
      fetchUser: async () => {
        try {
          const response = await api.get('/auth/me');
          const { user, company } = response.data;
          
          set({ user, company, isLoading: false });
          return { success: true };
        } catch (error) {
          if (error.response?.status === 401) {
            // Try to refresh token
            const refreshed = await get().refreshAccessToken();
            if (refreshed) {
              return get().fetchUser();
            }
          }
          set({ isLoading: false });
          return { success: false };
        }
      },
      
      // Refresh company data (useful when admin updates company settings)
      refreshCompany: async () => {
        try {
          console.log('[AuthStore] Refreshing company data...');
          const { selectedCompany, user } = get();
          
          // For Super/Managing Admins with a selected company, fetch that company's latest data
          if (user && ['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role) && selectedCompany?._id) {
            console.log('[AuthStore] Super Admin - fetching selected company:', selectedCompany._id);
            try {
              const companyResponse = await api.get(`/admin/companies/${selectedCompany._id}`);
              const updatedCompany = companyResponse.data;
              
              console.log('[AuthStore] Received selected company:', {
                _id: updatedCompany?._id,
                name: updatedCompany?.name,
                billingPlan: updatedCompany?.billingPlan,
                subscriptionPlanId: updatedCompany?.subscriptionPlanId
              });
              
              set({ selectedCompany: updatedCompany });
              console.log('[AuthStore] Selected company state updated');
              return { success: true, company: updatedCompany };
            } catch (err) {
              console.error('[AuthStore] Failed to fetch selected company:', err);
              // Fall through to refresh user's own company
            }
          }
          
          // For regular users or fallback, refresh from /auth/me
          const response = await api.get('/auth/me');
          const { company } = response.data;
          
          console.log('[AuthStore] Received company from /auth/me:', {
            _id: company?._id,
            name: company?.name,
            billingPlan: company?.billingPlan,
            subscriptionPlanId: company?.subscriptionPlanId
          });
          
          // Update the main company
          set({ company });
          
          // If a super user has selected this company, update that too
          if (selectedCompany && selectedCompany._id === company?._id) {
            set({ selectedCompany: company });
          }
          
          console.log('[AuthStore] Company state updated');
          return { success: true, company };
        } catch (error) {
          console.error('Failed to refresh company:', error);
          return { success: false };
        }
      },
      
      // Update user preferences
      updatePreferences: (preferences) => {
        const { user } = get();
        if (user) {
          set({
            user: {
              ...user,
              preferences: { ...user.preferences, ...preferences }
            }
          });
        }
      },
      
      // Update user profile in store (after successful API update)
      updateUserProfile: (updatedUser) => {
        const { user } = get();
        if (user) {
          set({
            user: {
              ...user,
              ...updatedUser
            }
          });
        }
      },
      
      // Check if user has permission
      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        
        // Super admins have all permissions
        if (user.role === 'SUPER_ADMIN') return true;
        if (user.role === 'MANAGING_ADMIN' && permission !== 'delete_super_admin') return true;
        
        // Check custom permissions
        if (user.customPermissions?.granted?.includes(permission)) return true;
        if (user.customPermissions?.revoked?.includes(permission)) return false;
        
        // Role-based permissions would be checked here
        // For simplicity, we'll do basic checks
        const rolePermissions = {
          ADMIN: ['manage_company_users', 'manage_users', 'manage_own_company', 'manage_data_sources', 'view_data_sources', 'create_dashboards', 'edit_dashboards', 'delete_dashboards', 'share_dashboards', 'view_dashboards', 'filter_data', 'export_data', 'use_ai', 'view_billing', 'view_audit_logs'],
          DATA_ANALYST: ['view_data_sources', 'create_dashboards', 'edit_dashboards', 'delete_dashboards', 'share_dashboards', 'view_dashboards', 'filter_data', 'export_data', 'use_ai'],
          VIEWER: ['view_dashboards', 'filter_data']
        };
        
        return rolePermissions[user.role]?.includes(permission) || false;
      },
      
      // Initialize auth state (called after rehydration)
      initialize: async () => {
        const { accessToken, _hasHydrated } = get();
        
        // Don't initialize until hydration is complete
        if (!_hasHydrated) {
          return;
        }
        
        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }
        
        // We have a token, so assume authenticated until proven otherwise
        set({ isAuthenticated: true });
        
        // Temporarily restore regional API endpoint from session storage
        // This allows initial API calls to use the last known endpoint
        restoreApiEndpoint();
        
        // Fetch user to validate the token and get user data
        const result = await get().fetchUser();
        if (!result.success) {
          // Token was invalid, clear auth state
          set({ isAuthenticated: false, accessToken: null, refreshToken: null });
          resetApiEndpoint(); // Clear any restored endpoint
        } else {
          // ALWAYS fetch fresh endpoint based on current selectedCompany
          // This ensures the endpoint matches the company's region, not a stale cached value
          updateApiEndpoint().catch(err => {
            console.warn('[Auth] Regional routing update failed:', err);
          });
        }
      },
      
      // Mark hydration complete
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      }
    }),
    {
      name: 'neoshift-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        selectedCompany: state.selectedCompany
      }),
      onRehydrateStorage: () => (state) => {
        // Called when the storage has been rehydrated
        if (state) {
          state.setHasHydrated(true);
          state.initialize();
        }
      }
    }
  )
);

